<script setup lang="ts" generic="T extends string | number | symbol">
import { computed, ref, watch } from 'vue';
import type { Ref } from 'vue';
import {
  onClickOutside,
  useElementBounding,
  useWindowSize,
} from '@vueuse/core';
import { cloneDeep } from 'lodash';

import { AppFadeInOutTransition } from '../transitions';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import AppSelectDropdown from './AppSelectDropdown.vue';
import type { AppSelectOption } from './AppSelect.model';
import {
  getDropdownOpeningDirection,
  getDropdownOpeningAlignment,
} from './AppSelect.helper';
import type {
  DropdownSize,
  DropdownOpeningDirection,
  DropdownOpeningAlignment,
} from './AppSelect.helper';

export interface AppSelectLightProps<T> {
  options: AppSelectOption<T>[];
  placeholder?: string;
  selected?: AppSelectOption<T>;
  currentLabel?: string;
  disabled?: boolean;
}

const props = defineProps<AppSelectLightProps<T>>();

interface AppSelectLightEmits {
  (eventName: 'select', value: AppSelectOption<T>): void;
  (eventName: 'open'): void;
}

const emit = defineEmits<AppSelectLightEmits>();

let displayedOptions = cloneDeep(props.options);
// NOTE: Vue incorrectly types code for generic components.
// Type casting to correct type makes type system happy again
// https://github.com/vuejs/composition-api/issues/483
let selected = ref(props.selected) as Ref<AppSelectOption<T> | undefined>;

const appSelectLightRef = ref(null);
const appSelectLightContainerRef = ref(null);
const isDropdownOpened = ref(false);
const dropdownOpeningDirection = ref<DropdownOpeningDirection>('open-below');
const dropdownOpeningAlignment = ref<DropdownOpeningAlignment>('align-left');

const selectedOptionLabel = computed(
  () => props.currentLabel || selected.value?.label || props.placeholder
);

const { height, width } = useWindowSize();

function toggleDropdown(): void {
  if (props.disabled) {
    return;
  }

  isDropdownOpened.value = !isDropdownOpened.value;
  if (isDropdownOpened.value && props.options.length > 1) {
    emit('open');
  }
}

function selectOption(option: AppSelectOption<T>): void {
  isDropdownOpened.value = false;
  selected.value = option;
  emit('select', option);
}

onClickOutside(appSelectLightRef, (event) => {
  const dropdown = document.querySelector('.app-select-dropdown');
  if (dropdown && dropdown.contains(event.target as Node)) {
    return;
  }
  if (isDropdownOpened.value) {
    isDropdownOpened.value = false;
  }
});

function calculateDropdownOpeningDirection(dropdownSize: DropdownSize): void {
  if (dropdownSize.height.value > 0) {
    const selectElementBounding = useElementBounding(appSelectLightRef);
    dropdownOpeningDirection.value = getDropdownOpeningDirection(
      height.value,
      dropdownSize.height.value,
      selectElementBounding.bottom.value
    );
  }
  if (dropdownSize.width.value > 0) {
    const selectElementBounding = useElementBounding(appSelectLightRef);
    dropdownOpeningAlignment.value = getDropdownOpeningAlignment(
      width.value,
      dropdownSize.height.value,
      selectElementBounding.right.value
    );
  }
}

watch(
  () => props.selected,
  () => {
    displayedOptions = cloneDeep(props.options);
    selected.value = props.selected;
  }
);
</script>

<template>
  <div
    ref="appSelectLightRef"
    :class="[{ disabled: props.disabled || props.options.length < 2 }]"
    class="app-select-light"
  >
    <div
      ref="appSelectLightContainerRef"
      class="app-select-light-container"
      @click="props.options.length > 1 && toggleDropdown()"
    >
      <div :class="{ 'app-select-label': true, placeholder: !selected }">
        {{ selectedOptionLabel }}
      </div>
      <div
        v-if="props.options.length > 1 && !props.disabled"
        class="right-side-icon"
      >
        <AppSvgIcon v-if="isDropdownOpened" name="chevronUp" size="24" />
        <AppSvgIcon v-if="!isDropdownOpened" name="chevronDown" size="24" />
      </div>
    </div>
    <Teleport v-if="isDropdownOpened" to="body">
      <AppFadeInOutTransition>
        <AppSelectDropdown
          :app-select-container-ref="appSelectLightContainerRef"
          type="light"
          :options="displayedOptions"
          :selected="props.selected"
          :dropdown-opening-direction="dropdownOpeningDirection"
          :dropdown-opening-alignment="dropdownOpeningAlignment"
          @select="selectOption"
          @dropdown-size="calculateDropdownOpeningDirection"
        />
      </AppFadeInOutTransition>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
@import '../../styles/_media';

.app-select-light {
  position: relative;
  display: flex;
  flex-flow: column;
  white-space: nowrap;
  background-color: var(--color-white);
  color: var(--color-secondary-40);
  font: var(--font-label-l-bold);

  transition: background-color var(--transition-short);
  width: 100%;
  cursor: pointer;

  &.disabled {
    cursor: default;
    transition: none;
    color: var(--color-secondary-60);
    .app-select-light-container {
      border: 1px solid var(--color-secondary-60);
    }
  }

  &:not(.disabled) {
    @include on-hover {
      background-color: var(--color-secondary-97);
    }

    &:active {
      background-color: var(--color-white);
      .app-select-light-container {
        box-shadow: inset 0 0 0 2px var(--color-secondary-40);
      }
    }
  }

  .app-select-light-container {
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
    padding: var(--spacing-12);
    justify-content: space-between;
    border: 1px solid var(--color-secondary-85);
    border-radius: var(--spacing-8);
  }
}
</style>
